import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

// Types
interface WishlistItem {
  _id: string;
  classId: string;
  userId: string;
  createdAt: string;
}

interface WishlistResponse {
  success: boolean;
  wishlist: WishlistItem[];
}

// API functions
async function fetchClassWishlist(): Promise<string[]> {
  const response = await fetch("/api/wishlist", {
    credentials: "include",
  });

  if (response.status === 401) {
    return [];
  }

  if (!response.ok) {
    throw new Error("Failed to fetch wishlist");
  }

  const data: WishlistResponse = await response.json();
  // Map classId field from API response
  return data.wishlist.map(
    (item: WishlistItem) =>
      item.classId || (item as { itemId?: string }).itemId || ""
  );
}

// Custom hooks

/**
 * Hook to fetch and manage class wishlist
 */
export function useClassWishlist() {
  return useQuery<string[], Error>({
    queryKey: ["wishlist", "classes"],
    queryFn: fetchClassWishlist,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401s (though we handle them above, just in case)
      if (error.message.includes("401")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to check if a specific class is in wishlist
 */
export function useIsClassInWishlist(classId: string) {
  const { data: wishlist = [] } = useClassWishlist();
  return wishlist.includes(classId);
}

/**
 * Hook to toggle class wishlist status (add/remove)
 */
export function useToggleClassWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ classId, action }: { classId: string; action: "add" | "remove" }) => {
      const response = await fetch("/api/wishlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ classId, action }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update wishlist");
      }

      await response.json();
      return {
        action: action === "add" ? "added" : ("removed" as "added" | "removed"),
        classId,
      };
    },
    onMutate: async ({ classId, action }) => {
      // Cancel outgoing refetches
      const queryKey = ["wishlist", "classes"];
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousWishlist = queryClient.getQueryData<string[]>(queryKey);

      // Optimistically update
      queryClient.setQueryData<string[]>(queryKey, (old = []) => {
        if (action === "remove") {
          return old.filter((id) => id !== classId);
        } else {
          // Avoid duplicates just in case
          return old.includes(classId) ? old : [...old, classId];
        }
      });

      return { previousWishlist, queryKey };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousWishlist);
      }
      toast.error(error.message || "Failed to update wishlist");
    },
    onSuccess: (data) => {
      if (data.action === "added") {
        toast.success("Class added to wishlist");
      } else {
        toast.success("Class removed from wishlist");
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["wishlist", "classes"] });
    },
  });
}

